import type { QueryClient } from "@tanstack/react-query";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import { fetchMediaAsset } from "@/lib/utils/media/fetch-media-asset";
import { getMediaAssetQueryKey } from "@/lib/utils/media/media-asset-query-key";
import {
  PUBLIC_PRESIGNED_URL_GC_MS,
  PUBLIC_PRESIGNED_URL_STALE_MS,
} from "@/lib/utils/media/public-media-cache";

export async function prefetchLocationCoverMedia(
  queryClient: QueryClient,
  tenantId: number,
  locationId: number,
) {
  if (tenantId <= 0 || locationId <= 0) return queryClient;

  await queryClient.prefetchQuery({
    queryKey: getMediaAssetQueryKey(
      tenantId,
      EntryType.Location,
      locationId,
      MediaGroup.Cover,
      "public",
    ),
    queryFn: () =>
      fetchMediaAsset({
        tenantId,
        ownerType: EntryType.Location,
        ownerId: locationId,
        mediaGroup: MediaGroup.Cover,
      }).catch(() => ({
        url: null,
        mediaId: null,
        media: null,
      })),
    staleTime: PUBLIC_PRESIGNED_URL_STALE_MS,
    gcTime: PUBLIC_PRESIGNED_URL_GC_MS,
  });

  return queryClient;
}

/** Prefetch cover images for a locations listing (avoids per-card client N+1). */
export async function prefetchLocationCoverMediaMany(
  queryClient: QueryClient,
  tenantId: number,
  locationIds: ReadonlyArray<unknown>,
) {
  if (tenantId <= 0) return queryClient;

  const uniqueIds = [
    ...new Set(
      locationIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    ),
  ];

  await Promise.all(
    uniqueIds.map((locationId) =>
      prefetchLocationCoverMedia(queryClient, tenantId, locationId),
    ),
  );

  return queryClient;
}
